// src/About.jsx

const About = ({ onBack }) => {
  return (
    <section style={{
      padding: "clamp(64px, 10vh, 120px) 24px 80px",
      maxWidth: "720px", margin: "0 auto",
    }}>
      <button
        onClick={onBack}
        aria-label="Go back"
        style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: "14px", color: "var(--ink-muted)", fontFamily: "inherit",
          marginBottom: "32px", padding: 0,
        }}
        onMouseEnter={(e) => e.target.style.color = "var(--ink)"}
        onMouseLeave={(e) => e.target.style.color = "var(--ink-muted)"}
      >
        &larr; Back
      </button>

      <p style={{
        fontSize: "13px", fontWeight: "500", color: "var(--accent)",
        letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "16px",
      }}>Our story</p>

      <h1 style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: "clamp(28px, 4vw, 40px)", fontWeight: "400",
        color: "var(--ink)", letterSpacing: "-0.02em",
        lineHeight: 1.2, marginBottom: "32px",
        maxWidth: "560px",
      }}>
        Named for a cancer survivor. Built for every family facing medical bills.
      </h1>

      <div style={{
        fontSize: "16px", lineHeight: 1.85, color: "var(--ink-soft)",
        display: "flex", flexDirection: "column", gap: "24px",
      }}>
        <p>
          In 2024, at 39 years old, Irene was diagnosed with triple positive breast cancer.
          She was scheduled to get her chemo port placed on her birthday — but she didn't
          want a completely miserable birthday, so she pushed it out a week. That small act
          of defiance tells you everything you need to know about her. What followed was
          months of chemo, a unilateral mastectomy, more immunotherapy/chemo, and DIEP flap
          reconstruction. It was the hardest thing our family has ever been through.
        </p>

        <p>
          But the cancer was only half the battle. The bills started arriving before
          the treatment ended. EOBs we couldn't decipher. Charges that didn't match
          what we were told. Insurance denials for things we thought were covered.
          Collection notices for bills we didn't know existed.
        </p>

        <p>
          We spent hundreds of hours on the phone with insurance companies, hospital
          billing departments, and financial assistance offices. We learned things
          the hard way that every family should know from day one: that most
          nonprofit hospitals are legally required to offer financial assistance, that
          80% of medical bills contain errors, that you should never pay a medical
          bill without checking it first.
        </p>

        <p>
          Irene is cancer-free now. But the billing doesn't stop. And we know that
          right now, millions of families are going through exactly what we went
          through — overwhelmed, confused, and feeling completely alone in a system
          that wasn't designed to be understood.
        </p>

        <div style={{
          borderLeft: "3px solid var(--accent)",
          paddingLeft: "20px",
          margin: "8px 0",
        }}>
          <p style={{
            fontSize: "18px", lineHeight: 1.7, color: "var(--ink)",
            fontFamily: "'DM Serif Display', serif", fontWeight: "400",
            fontStyle: "italic",
          }}>
            We built Ask Irene because no family should have to figure this out alone.
          </p>
        </div>

        <p>
          Ask Irene is an AI advisor trained on everything we wish we'd known from
          the start. She knows how to read an EOB, when to dispute a charge, how to
          apply for financial assistance, and what your rights are as a patient. She
          speaks in plain English, never jargon. And she's available 24/7, because
          billing anxiety doesn't keep business hours.
        </p>

        <div style={{
          backgroundColor: "var(--surface-warm)",
          border: "1px solid var(--border)",
          borderRadius: "8px",
          padding: "24px",
          margin: "8px 0",
        }}>
          <h2 style={{
            fontSize: "18px", fontWeight: "600", color: "var(--ink)",
            marginBottom: "12px", fontFamily: "'DM Serif Display', serif",
          }}>
            Where your subscription goes
          </h2>
          <p style={{ marginBottom: "12px" }}>
            The free tier of Ask Irene will always be free. We believe that access
            to medical billing guidance is a right, not a privilege.
          </p>
          <p>
            Pro subscriptions fund the infrastructure that makes this possible: the
            AI that powers Irene's responses, the servers that keep her available
            around the clock, and the ongoing development of new features like bill
            analysis and document upload. Every subscriber helps us keep the free
            tier available to families who need it most.
          </p>
        </div>

        <p>
          Ask Irene is a small, independent project built by the family behind the
          name. We're not a corporation. We're not backed by venture capital. We're
          a family that went through something hard and decided to turn what we
          learned into something that could help others.
        </p>

        <p style={{ color: "var(--ink)" }}>
          Thank you for being here. Whether you're using the free chat or supporting
          us as a Pro subscriber, you're part of something that matters.
        </p>

        <p style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: "18px", color: "var(--ink)",
          marginTop: "8px",
        }}>
          — Blake &amp; Irene
        </p>

        <div style={{
          borderTop: "1px solid var(--border-light)",
          paddingTop: "24px", marginTop: "16px",
        }}>
          <h2 style={{
            fontSize: "18px", fontWeight: "600", color: "var(--ink)",
            marginBottom: "12px", fontFamily: "'DM Serif Display', serif",
          }}>
            In the news
          </h2>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
            {[
              {
                outlet: "The Courier",
                note: "Findlay, Ohio",
                url: "https://thecourier.com/news/1082632/after-cancer-journey-couple-helps-others-decipher-confusing-mess-of-bills/",
              },
              {
                outlet: "Bowling Green State University",
                note: "BGSU News",
                url: "https://www.bgsu.edu/news/2026/10/bgsu-falcon-flames-create-ai-powered-resource-to-help-cancer-patients-navigate-healthcare-costs.html",
              },
            ].map((item) => (
              <li key={item.url}>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--accent)", textDecoration: "underline", textUnderlineOffset: "2px" }}
                >
                  {item.outlet}
                  <span style={{ position: "absolute", left: "-9999px" }}> (opens in a new tab)</span>
                </a>
                <span style={{ color: "var(--ink-muted)", fontSize: "14px" }}> · {item.note}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default About;
